import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Gift, Copy, Check, Users, DollarSign, Share2, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function ReferralPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [codeRes, statsRes] = await Promise.all([
          axios.get(`${API}/referral/code`, { headers, withCredentials: true }),
          axios.get(`${API}/referral/stats`, { headers, withCredentials: true })
        ]);

        setReferralCode(codeRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching referral data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    await navigator.clipboard.writeText(referralCode.code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = async () => {
    if (!referralCode) return;
    const link = `${window.location.origin}/auth?ref=${referralCode.code}`;
    await navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/referral/apply`, 
        { referral_code: applyCode },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true 
        }
      );
      toast.success('Referral code applied! You\'ll get benefits when you upgrade.');
      setApplyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply code');
    } finally {
      setApplying(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="skeleton h-64 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Gift className="w-16 h-16 mx-auto mb-6 text-cyan-400" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-testid="referral-title">
            Referral Program
          </h1>
          <p className="text-white/50">
            Share the interconnected picture and earn rewards when friends subscribe to Premium.
          </p>
        </div>

        {/* Your Referral Code */}
        <div className="card-dark p-6 mb-8" data-testid="referral-code-card">
          <h2 className="font-heading text-xl font-semibold mb-4">Your Referral Code</h2>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-sm p-4 font-mono text-2xl text-center text-cyan-400 tracking-widest">
              {referralCode?.code || '---'}
            </div>
            <Button
              onClick={handleCopyCode}
              className="btn-outline"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>

          <Button
            onClick={handleCopyLink}
            className="w-full btn-primary flex items-center justify-center gap-2"
            data-testid="copy-link-btn"
          >
            <Share2 className="w-4 h-4" />
            Copy Referral Link
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="card-dark p-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-3 text-cyan-400" />
            <p className="text-3xl font-bold mb-1">{stats?.uses || 0}</p>
            <p className="text-white/40 text-sm">Referrals</p>
          </div>
          <div className="card-dark p-6 text-center">
            <DollarSign className="w-8 h-8 mx-auto mb-3 text-green-400" />
            <p className="text-3xl font-bold mb-1">${(stats?.earnings || 0).toFixed(2)}</p>
            <p className="text-white/40 text-sm">Earnings</p>
          </div>
        </div>

        {/* Referred Users */}
        {stats?.referrals?.length > 0 && (
          <div className="card-dark p-6 mb-8">
            <h3 className="font-heading text-lg font-semibold mb-4">Your Referrals</h3>
            <div className="space-y-3">
              {stats.referrals.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white">{ref.name}</p>
                    <p className="text-white/40 text-xs">
                      Joined {new Date(ref.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {ref.is_premium && (
                    <span className="flex items-center gap-1 text-yellow-500 text-xs">
                      <Crown className="w-4 h-4" />
                      Premium
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Apply Code */}
        <div className="card-dark p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Have a Referral Code?</h3>
          <div className="flex gap-4">
            <Input
              type="text"
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="flex-1 bg-white/5 border-white/10 font-mono uppercase"
              data-testid="apply-code-input"
            />
            <Button
              onClick={handleApplyCode}
              className="btn-outline"
              disabled={applying}
              data-testid="apply-code-btn"
            >
              {applying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
          <p className="text-white/30 text-xs mt-2">
            Apply before upgrading to Premium to receive special benefits.
          </p>
        </div>

        {/* How it works */}
        <div className="mt-8 p-6 bg-white/5 rounded-sm">
          <h3 className="font-heading text-lg font-semibold mb-4">How It Works</h3>
          <ol className="space-y-3 text-white/60 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-400 flex items-center justify-center text-xs flex-shrink-0">1</span>
              Share your unique referral code with friends
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-400 flex items-center justify-center text-xs flex-shrink-0">2</span>
              They sign up and apply your code before subscribing
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-400/20 text-cyan-400 flex items-center justify-center text-xs flex-shrink-0">3</span>
              When they upgrade to Premium, you both get rewarded
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
